import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import { FLOORPLAN_UUID_QUERY_PARAM } from 'floorplan/pages/native-floorplan/const';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';
import { Floorplan } from 'floorplan/pages/native-floorplan/types';
import { findActiveFloorplan } from 'floorplan/pages/native-floorplan/utils/active-floorplan';

interface UseActiveFloorplanValues {
  activeFloorplan: Floorplan | null;
  onFloorplanSelect: (floorplanUuid: string) => void;
}

/**
 * Resolves which floor (= which floorplan) the map is showing.
 *
 * The selection lives in the query string next to the date and filters, not in the path: the map is
 * a view of the resources page, so the floor has to survive alongside everything else already in the
 * url — and a reload or a shared link lands on the same floor.
 *
 * The uuid is resolved by `findActiveFloorplan`, which the filters sheet shares.
 */
export const useActiveFloorplan = (floorplans: Floorplan[], isEnabled = true): UseActiveFloorplanValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  const selectedUuid = queryParams[FLOORPLAN_UUID_QUERY_PARAM];

  const activeFloorplan = useMemo(
    () => (isEnabled ? findActiveFloorplan(floorplans, selectedUuid) : null),
    [isEnabled, floorplans, selectedUuid],
  );

  const buildUrl = useCallback(
    (floorplanUuid: string) =>
      `${pathname}?${qs.stringify({ ...queryParams, [FLOORPLAN_UUID_QUERY_PARAM]: floorplanUuid })}`,
    [pathname, queryParams],
  );

  // Reflect the resolved floor in the url — covers opening the map with no floor chosen and a stale
  // link to a floorplan that has since been deleted.
  useEffect(() => {
    // Only while the map is on screen — the list view has no floor, and writing one into its url
    // would leave a stale parameter behind.
    if (isEnabled && activeFloorplan && activeFloorplan.uuid !== selectedUuid) {
      dispatch(replace(buildUrl(activeFloorplan.uuid)));
    }
  }, [isEnabled, activeFloorplan, selectedUuid, buildUrl, dispatch]);

  const onFloorplanSelect = useCallback(
    (floorplanUuid: string) => dispatch(replace(buildUrl(floorplanUuid))),
    [buildUrl, dispatch],
  );

  return { activeFloorplan, onFloorplanSelect };
};
